import { AreaChart } from "@mantine/charts";
import { Flex } from "@mantine/core";
import type { JSX } from "react";
import { ActiveDot } from "../icons/ActiveDot";
import type { PayList, ScaleEntry } from "../types";
import { DynamicTooltip } from "./DynamicTooltipReplacement";
import classes from "./SharedChartProps.module.css";

interface ChartProps {
    value: number;
    payList: PayList;
    salesDropped: number;
    toggleTooltip: React.Dispatch<React.SetStateAction<boolean>>;
    staticShowing: boolean;
    isSales: boolean;
    isMetricBroken: boolean;
    interval: number;
}

export function SharedChart({
    value,
    payList,
    salesDropped,
    staticShowing,
    toggleTooltip,
    isSales,
    isMetricBroken,
    interval,
}: ChartProps): JSX.Element | null {
    let rawValue = value - (isSales ? salesDropped : 0);
    let netValue = rawValue < 0 ? 0 : rawValue;
    if (isMetricBroken) {
        rawValue = 0;
        netValue = 0;
    }

    if (payList.length === 0) {
        return null;
    }

    const buildDot = (props: unknown) => {
        const { cx, cy, payload } = props as {
            cx: number;
            cy: number;
            payload: { metric: number; pay: number; perMetricPay: number };
        };
        if (payload.metric === netValue) {
            return <ActiveDot xOffset={cx} yOffset={cy} />;
        }
        return <svg />;
    };

    payList.forEach((p) => {
        if (p.metric <= netValue) {
            p.leftSplit = p.pay;
            p.rightSplit = p.pay;
        } else {
            p.rightSplit = p.pay;
        }
    });

    return (
        <Flex key={`${isSales}${value}`} className={classes.chartComboArea} w="100%" h="220px">
            {payList.length > 1 && (
                <AreaChart
                    areaProps={{
                        dot: buildDot,
                    }}
                    dotProps={{ r: 8, strokeWidth: 1, fill: "transparent" }}
                    data={payList}
                    dataKey="metric"
                    fillOpacity={1}
                    series={[
                        {
                            name: "rightSplit",
                            color: "#333F48",
                        },
                        {
                            name: "leftSplit",
                            color: "#6700B8",
                        },
                    ]}
                    strokeWidth={3}
                    strokeDasharray="0 1"
                    w={`${String((payList.length / payList.length) * 100)}%`}
                    withTooltip={true}
                    xAxisProps={{ hide: false, padding: { left: 40, right: 15 }, interval: interval - 1 }}
                    yAxisProps={{
                        hide: true,
                        padding: { top: 12, bottom: 4 },
                    }}
                    onMouseLeave={() => {
                        toggleTooltip(true);
                    }}
                    tooltipProps={{
                        content: ({ label, payload }) => {
                            const mappedLabel = label as string;
                            const mappedPayload = payload as Record<string, ScaleEntry>[];
                            return DynamicTooltip({
                                label: mappedLabel,
                                payload: mappedPayload,
                                currentValue: rawValue,
                                staticShowing: staticShowing,
                                toggleStaticTooltip: toggleTooltip,
                                isSales,
                            });
                        },
                    }}
                />
            )}
        </Flex>
    );
}
